import { useState, useRef, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ticketsApi } from "../utils/api";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";

const steps = ["Attendee Details", "Review & Pay", "Confirmed"];

export default function BookingPage() {
  const { slug, tierId, qty } = useParams();
  const navigate  = useNavigate();
  const quantity  = parseInt(qty) || 1;
  const formRef   = useRef(null);

  const [event, setEvent]     = useState(null);
  const [tier, setTier]       = useState(null);
  const [step, setStep]       = useState(0);
  const [attendees, setAttendees] = useState(
    Array.from({length:quantity}, () => ({name:"", whatsapp:"", email:""}))
  );
  const [errors, setErrors]   = useState({});
  const [processing, setProcessing] = useState(false);
  const [orderRef, setOrderRef]     = useState("");
  const [tickets, setTickets]         = useState([]);
  const [payhereParams, setPayhereParams] = useState(null);
  const [apiError, setApiError] = useState("");
  const [promoCode, setPromoCode]   = useState("");
  const [payMethod, setPayMethod]     = useState("bank"); // bank only
  const [stripeClientSecret, setStripeClientSecret] = useState("");
  const [stripeKey, setStripeKey] = useState("");
  const [bankDone, setBankDone]       = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [promoError, setPromoError]   = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  const handleBankTransfer = async () => {
    setProcessing(true); setApiError("");
    try {
      const d = await ticketsApi.initiateBankTransfer({
        eventId: event.id, tierId: tier.id, quantity,
        attendees: attendees.map(a => ({name:a.name, whatsapp:a.whatsapp, email:a.email}))
      });
      setOrderRef(d.orderRef);
      setBankDone(true);
      setStep(2);
    } catch(err) { setApiError(err.message); }
    finally { setProcessing(false); }
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true); setPromoError(""); setPromoResult(null);
    try {
      const d = await ticketsApi.validatePromo(promoCode, event?.id, subtotal);
      setPromoResult(d.promo);
    } catch(e) { setPromoError(e.message); }
    finally { setPromoLoading(false); }
  };





  useEffect(() => {
    if (step === 2 && orderRef && tickets.length === 0) {
      ticketsApi.getOrder(orderRef)
        .then(d => setTickets(d.tickets || []))
        .catch(() => {});
    }
  }, [step, orderRef]);

  useEffect(() => {
    ticketsApi.getEvent(slug).then(d => {
      setEvent(d.event);
      setTier(d.event.tiers?.find(t => t.id === parseInt(tierId)));
    }).catch(() => navigate("/"));
  }, [slug, tierId]);

  if (!event || !tier) return (
    <div className="min-h-screen flex items-center justify-center" style={{background:"#07000f"}}>
      <div className="w-12 h-12 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
    </div>
  );

  const subtotal = parseInt(tier.price) * quantity;
  const fee      = 0;
  const total    = subtotal;
  const finalTotal = promoResult ? promoResult.finalAmount : total;

  const update = (i, field, val) => {
    const a = [...attendees]; a[i] = {...a[i], [field]:val}; setAttendees(a);
    const e = {...errors}; delete e[`${i}-${field}`]; setErrors(e);
  };

  const validate = () => {
    const errs = {};
    attendees.forEach((a, i) => {
      if (!a.name.trim())     errs[`${i}-name`]     = "Full name is required";
      if (!a.whatsapp.trim()) errs[`${i}-whatsapp`] = "WhatsApp number is required";
      else if (!/^\+?[\d\s]{9,15}$/.test(a.whatsapp)) errs[`${i}-whatsapp`] = "Enter a valid number";
      if (!a.email.trim())    errs[`${i}-email`]    = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) errs[`${i}-email`] = "Enter a valid email";
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleInitiate = async () => {
    setProcessing(true); setApiError("");
    try {
      if (payMethod === "stripe") {
        const d = await ticketsApi.createStripeIntent({
          eventId: event.id, tierId: tier.id, quantity,
          attendees: attendees.map(a => ({name:a.name, whatsapp:a.whatsapp, email:a.email}))
        });
        setOrderRef(d.orderRef);
        setStripeClientSecret(d.clientSecret);
        setStripeKey(d.publishableKey);
        setStep(1);
      } else if (payMethod === "bank") {
        const d = await ticketsApi.initiateBankTransfer({
          eventId: event.id, tierId: tier.id, quantity,
          attendees: attendees.map(a => ({name:a.name, whatsapp:a.whatsapp, email:a.email}))
        });
        setOrderRef(d.orderRef); setStep(1);
      } else {
        const d = await ticketsApi.initiateOrder({
          eventId: event.id, tierId: tier.id, quantity,
          attendees: attendees.map(a => ({name:a.name, whatsapp:a.whatsapp, email:a.email}))
        });
        setOrderRef(d.orderRef); setPayhereParams(d.payhereParams); setStep(1);
      }
    } catch(err) { setApiError(err.message); }
    finally { setProcessing(false); }
  };

  const PAYHERE_URL = payhereParams?.sandbox
    ? "https://sandbox.payhere.lk/pay/checkout"
    : "https://www.payhere.lk/pay/checkout";

  const inp = (errKey) => ({
    background: errors[errKey] ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.05)",
    border: `1px solid ${errors[errKey] ? "#ef4444" : "rgba(255,255,255,0.1)"}`,
  });

  return (
    <div className="min-h-screen pt-20 pb-16 px-4" style={{background:"#07000f"}}>


      <div className="max-w-2xl mx-auto relative z-10">
        {/* Back */}
        <Link to={`/events/${slug}`} className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-8 transition group">
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
          </svg>
          Back to event
        </Link>

        {/* Progress steps */}
        <div className="flex items-center mb-10">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  i < step  ? "bg-green-500 text-white" :
                  i === step ? "text-white" : "bg-white/10 text-gray-500"
                }`}
                style={i === step ? {background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"} : {}}>
                  {i < step ? "✓" : i + 1}
                </div>
                <span className={`text-sm hidden sm:block font-medium ${i === step ? "text-white" : i < step ? "text-green-400" : "text-gray-600"}`}>{s}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-px mx-4 transition-all duration-300 ${i < step ? "bg-green-500/50" : "bg-white/10"}`}/>
              )}
            </div>
          ))}
        </div>

        {/* Event summary card */}
        <div className="rounded-2xl p-4 flex gap-4 mb-8" style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)"}}>
          {(event.thumb_image || event.banner_image) && (
            <img src={event.thumb_image||event.banner_image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0"/>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-white">{event.title}</p>
            <p className="text-gray-400 text-xs mt-0.5">{event.event_date} · {event.venue_name}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-brand-300 font-medium">{tier.name}</span>
              <span className="text-xs text-gray-500">×{quantity} ticket{quantity>1?"s":""}</span>
              <span className="text-xs font-bold text-white ml-auto">LKR {total.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* API Error */}
        {apiError && (
          <div className="rounded-xl p-4 mb-6 flex items-center gap-3" style={{background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)"}}>
            <span className="text-xl">⚠️</span>
            <p className="text-red-300 text-sm">{apiError}</p>
          </div>
        )}

        {/* ── STEP 0: Attendee Details ── */}
        {step === 0 && (
          <div>
            <div className="mb-6">
              <h2 className="font-display text-xl md:text-2xl font-bold text-white mb-1">Attendee Details</h2>
              <p className="text-gray-400 text-sm">
                {quantity > 1
                  ? `Each ticket needs individual details. Each person receives their own QR code.`
                  : "Enter your details. Your QR ticket will be sent to WhatsApp and email."}
              </p>
            </div>

            <div className="space-y-4">
              {attendees.map((a, i) => (
                <div key={i} className="rounded-2xl overflow-hidden" style={{border:"1px solid rgba(255,255,255,0.08)"}}>
                  {/* Attendee header */}
                  <div className="px-5 py-3 flex items-center gap-3" style={{background:"rgba(155,63,232,0.08)", borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                      {i + 1}
                    </div>
                    <span className="text-white font-semibold text-sm">
                      {quantity > 1 ? `Attendee ${i+1}` : "Your Details"}
                    </span>
                    {i === 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium ml-1"
                        style={{background:"rgba(155,63,232,0.2)", color:"#d49dff", border:"1px solid rgba(155,63,232,0.3)"}}>
                        Primary
                      </span>
                    )}
                  </div>

                  {/* Fields */}
                  <div className="p-5 space-y-4" style={{background:"rgba(255,255,255,0.02)"}}>
                    {[
                      {label:"Full Name",       field:"name",     type:"text",  placeholder:"e.g. Dilshan Perera",   icon:"👤"},
                      {label:"WhatsApp Number", field:"whatsapp", type:"tel",   placeholder:"+94 77 123 4567",       icon:"📱"},
                      {label:"Email Address",   field:"email",    type:"email", placeholder:"dilshan@email.com",     icon:"📧"},
                    ].map(f => (
                      <div key={f.field}>
                        <label className="text-xs text-gray-400 mb-1.5 block font-medium">{f.icon} {f.label} *</label>
                        <input type={f.type} placeholder={f.placeholder} value={a[f.field]}
                          onChange={e => {
                          let v = e.target.value;
                          if (f.field === 'whatsapp') {
                            v = v.replace(/[^0-9+]/g,'');
                            if (v.startsWith('0')) v = '+94' + v.slice(1);
                            else if (v.length > 0 && !v.startsWith('+')) v = '+94' + v;
                          }
                          update(i, f.field, v);
                        }}
                          className="w-full px-4 py-3.5 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all"
                          style={inp(`${i}-${f.field}`)}/>
                        {errors[`${i}-${f.field}`] && (
                          <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                            <span>⚠</span> {errors[`${i}-${f.field}`]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button onClick={() => validate() && handleInitiate()} disabled={processing}
              className="w-full mt-6 py-4 rounded-2xl font-bold text-white text-lg transition-all hover:scale-[1.01] disabled:opacity-60 disabled:scale-100"
              style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)", boxShadow:"0 8px 32px rgba(124,34,200,0.3)"}}>
              {processing ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Saving details...
                </span>
              ) : "Continue to Payment →"}
            </button>

            <p className="text-center text-gray-600 text-xs mt-4">
              🔒 Your details are encrypted and secure
            </p>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="font-display text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Review & Pay</h2>

            {/* Attendees review */}
            <div className="rounded-2xl overflow-hidden mb-4" style={{border:"1px solid rgba(255,255,255,0.08)"}}>
              <div className="px-5 py-3" style={{background:"rgba(255,255,255,0.03)", borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                <p className="text-white font-semibold text-sm">Attendees</p>
              </div>
              <div className="divide-y divide-white/5">
                {attendees.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 px-5 py-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                      {i+1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{a.name}</p>
                      <p className="text-gray-500 text-xs">{a.email} · {a.whatsapp}</p>
                    </div>
                    <span className="text-xs text-brand-300">{tier.name}</span>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3" style={{background:"rgba(255,255,255,0.02)", borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                <button onClick={() => setStep(0)} className="text-xs text-gray-500 hover:text-gray-300 transition">
                  ✏️ Edit attendee details
                </button>
              </div>
            </div>

            {/* Order total */}
            <div className="rounded-2xl p-5 mb-4" style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)"}}>
              <p className="text-white font-semibold text-sm mb-3">Order Total</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">{tier.name} × {quantity}</span><span className="text-white">LKR {subtotal.toLocaleString()}</span></div>

                {promoResult && (
                  <div className="flex justify-between text-green-400">
                    <span>Promo discount</span>
                    <span>- LKR {promoResult.discount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t border-white/10 font-bold text-base">
                  <span className="text-white">Total</span>
                  <span style={{background:"linear-gradient(135deg,#d49dff,#f59e0b)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent"}}>
                    LKR {finalTotal.toLocaleString()}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-3 font-mono">Ref: {orderRef}</p>
            </div>

            {/* Promo Code */}
            <div className="rounded-2xl p-5 mb-4" style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)"}}>
              <p className="text-white font-semibold text-sm mb-3">🏷️ Promo Code</p>
              {promoResult ? (
                <div className="flex items-center justify-between p-3 rounded-xl" style={{background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.3)"}}>
                  <div>
                    <p className="text-green-300 font-bold text-sm">{promoResult.code} applied!</p>
                    <p className="text-green-400/70 text-xs">
                      {promoResult.type === "percent" ? `${promoResult.value}% off` : `LKR ${promoResult.value} off`}
                      {" — "}saving LKR {promoResult.discount.toLocaleString()}
                    </p>
                  </div>
                  <button onClick={() => { setPromoResult(null); setPromoCode(""); }}
                    className="text-xs text-gray-500 hover:text-red-400 transition">Remove</button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <input type="text" placeholder="Enter promo code" value={promoCode}
                      onChange={e => setPromoCode(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === "Enter" && applyPromo()}
                      className="flex-1 px-4 py-2.5 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 font-mono uppercase"
                      style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)"}}/>
                    <button onClick={applyPromo} disabled={promoLoading}
                      className="px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition hover:opacity-90 disabled:opacity-60"
                      style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                      {promoLoading ? "..." : "Apply"}
                    </button>
                  </div>
                  {promoError && <p className="text-red-400 text-xs mt-1.5">⚠️ {promoError}</p>}
                </div>
              )}
            </div>


            {/* Payment Method - Bank Transfer Only */}
            <div className="mb-4">
              <div className="rounded-2xl p-4" style={{background:"rgba(34,197,94,0.08)", border:"2px solid rgba(34,197,94,0.3)"}}>
                <p className="text-white font-semibold text-sm mb-1">🏦 Bank Transfer</p>
                <p className="text-gray-400 text-xs">Commercial Bank of Ceylon · Manual verification</p>
              </div>
            </div>

            {/* Bank Transfer Details + Upload */}
            <div className="rounded-2xl p-5 mb-4" style={{background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.2)"}}>
              <p className="text-green-300 font-semibold text-sm mb-3">🏦 Bank Transfer Details</p>
              <div className="space-y-2 text-sm mb-4">
                {[
                  {l:"Bank",       v:"Commercial Bank of Ceylon"},
                  {l:"Account",    v:"W.K. Deshan"},
                  {l:"Acc Number", v:"8024355670"},
                  {l:"Branch",     v:"Bambalapitiya"},
                  {l:"Amount",     v:`LKR ${finalTotal.toLocaleString()}`},
                ].map(r => (
                  <div key={r.l} className="flex justify-between">
                    <span className="text-gray-400">{r.l}</span>
                    <span className="text-white font-medium">{r.v}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-xl mb-4" style={{background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.2)"}}>
                <p className="text-yellow-300 text-xs">Transfer the exact amount and upload your receipt. Ticket will be sent after confirmation.</p>
              </div>
              <BankReceiptUploadInline
                orderRef={orderRef}
                attendees={attendees}
                eventId={event?.id}
                tierId={tier?.id}
                quantity={quantity}
                finalTotal={finalTotal}
                onDone={() => setStep(2)}
              />
            </div>


            <p className="text-center text-gray-600 text-xs mt-4">
              By paying you agree to our terms of service. All sales are final.
            </p>
          </div>
        )}

        {/* ── STEP 2: Confirmed ── */}
        {step === 2 && (
          <div className="text-center">
            <div className="relative mx-auto mb-6 w-32 h-32 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full animate-ping opacity-20"
                style={{background: payMethod==="bank" ? "#f59e0b" : "#22c55e"}}/>
              <div className="w-24 h-24 rounded-full flex items-center justify-center text-5xl"
                style={{background: payMethod==="bank" ? "rgba(245,158,11,0.15)" : "rgba(34,197,94,0.15)", border: payMethod==="bank" ? "2px solid rgba(245,158,11,0.4)" : "2px solid rgba(34,197,94,0.4)"}}>
                {payMethod==="bank" ? "⏳" : "🎉"}
              </div>
            </div>
            <h2 className="font-display text-4xl font-bold text-white mb-2">{payMethod==="bank" ? "Receipt Submitted!" : "Booking Confirmed!"}</h2>
            <p className="text-gray-400 mb-1">{payMethod==="bank" ? "We will verify and send your ticket within 2 hours" : "Your QR tickets are ready"}</p>
            <p className="text-brand-300 text-sm font-mono mb-8">{orderRef}</p>

            <div className="rounded-2xl p-6 mb-8 text-left space-y-4" style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)"}}>
              {[
                {i:"📧", t:"Email confirmation",  d:`Sent to ${attendees[0]?.email}`},
                {i:"📱", t:"WhatsApp QR tickets", d:`Sent to all ${quantity} attendee${quantity>1?"s":""}`},
                {i:"🎟️", t:"QR codes ready",      d:`${quantity} unique QR code${quantity>1?"s":""} — one per attendee`},
                {i:"🚪", t:"At the venue",         d:"Show your QR at the entrance for instant check-in"},
              ].map(x => (
                <div key={x.t} className="flex gap-4">
                  <span className="text-2xl">{x.i}</span>
                  <div>
                    <p className="text-white font-semibold text-sm">{x.t}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{x.d}</p>
                  </div>
                </div>
              ))}
            </div>

            {payMethod === "bank" && (
              <div className="mb-4">
                <a href={`https://wa.me/94774651468?text=Hi%2C%20I%20have%20submitted%20my%20bank%20transfer%20receipt%20for%20order%20${orderRef}.%20Please%20confirm%20my%20ticket.`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl font-bold text-white text-lg transition-all hover:opacity-90"
                  style={{background:"linear-gradient(135deg,#166534,#25d366)", boxShadow:"0 8px 32px rgba(37,211,102,0.3)"}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.112 1.524 5.84L.057 23.94l6.278-1.648A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.368l-.36-.213-3.726.977.995-3.635-.234-.374A9.818 9.818 0 1112 21.818z"/></svg>
                  Send Receipt on WhatsApp
                </a>
              </div>
            )}

            {payMethod !== "bank" && (
              <div className="flex gap-3 mb-4">
                {tickets.length > 0 ? (
                  <>
                    <a href={`/api/tickets/ticket/${tickets[0].ticket_uid}/pdf`}
                      download={`ticket-${tickets[0].attendee_name}.pdf`}
                      className="flex-1 py-3.5 rounded-2xl text-white text-sm font-semibold transition text-center hover:opacity-90"
                      style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                      📄 Download PDF Ticket
                    </a>
                    <a href={`/uploads/tickets/qr/${tickets[0].ticket_uid}.png`}
                      download={`ticket-qr-${tickets[0].attendee_name}.png`}
                      className="flex-1 py-3.5 glass rounded-2xl text-gray-300 hover:text-white text-sm font-semibold transition text-center">
                      ⬇ Download QR
                    </a>
                  </>
                ) : (
                  <Link to="/my-tickets" className="flex-1 py-3.5 rounded-2xl text-white text-sm font-semibold transition text-center"
                    style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                    View & Download Tickets →
                  </Link>
                )}
              </div>
            )}
            <div className="flex gap-3">
              <Link to="/" className="flex-1 py-3 glass rounded-2xl text-gray-300 hover:text-white text-sm font-medium transition text-center">
                Browse Events
              </Link>
              <Link to="/my-tickets" className="flex-1 py-3 glass rounded-2xl text-gray-300 hover:text-white text-sm font-medium transition text-center">
                My Tickets
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function StripePaymentForm({ clientSecret, orderRef, amount, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cardStyle = {
    style: {
      base: {
        fontSize: "16px", color: "#ffffff",
        fontFamily: "Nunito, sans-serif",
        "::placeholder": { color: "#6b7280" },
        iconColor: "#9b3fe8",
      },
      invalid: { color: "#ef4444" }
    }
  };

  const fieldStyle = {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "12px",
    padding: "14px 16px",
  };

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setLoading(true); setError("");
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
        }
      });
      if (stripeError) { setError(stripeError.message); return; }
      if (paymentIntent.status === "succeeded") {
        await fetch("/api/tickets/stripe/confirm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderRef, paymentIntentId: paymentIntent.id })
        });
        onSuccess(orderRef);
      }
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="rounded-2xl p-5 mb-4" style={{background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)"}}>
        <p className="text-xs text-gray-400 mb-4 font-medium uppercase tracking-wider">Card Details</p>
        <div className="space-y-3">
          {/* Card Number */}
          <div>
            <p className="text-xs text-gray-500 mb-1.5">Card Number</p>
            <div style={fieldStyle}>
              <CardNumberElement options={cardStyle}/>
            </div>
          </div>
          {/* Expiry + CVV */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Expiry Date</p>
              <div style={fieldStyle}>
                <CardExpiryElement options={cardStyle}/>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">CVV</p>
              <div style={fieldStyle}>
                <CardCvcElement options={cardStyle}/>
              </div>
            </div>
          </div>
        </div>
      </div>
      {error && <p className="text-red-400 text-xs mb-3">⚠️ {error}</p>}
      <button onClick={handlePay} disabled={loading || !stripe}
        className="w-full py-4 rounded-2xl font-bold text-white text-lg transition-all hover:scale-[1.01] disabled:opacity-60"
        style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)", boxShadow:"0 8px 32px rgba(124,34,200,0.3)"}}>
        {loading ? "Processing..." : `Pay LKR ${amount.toLocaleString()}`}
      </button>
      <p className="text-center text-gray-600 text-xs mt-3">🔒 Secured by Stripe</p>
    </div>
  );
}

function BankReceiptUpload({ orderRef, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState("");

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setError("");
    try {
      const formData = new FormData();
      formData.append("receipt", file);
      const res = await fetch(`/api/tickets/orders/${orderRef}/upload-receipt`, {
        method: "POST", body: formData,
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error);
      setUploaded(true);
      setTimeout(() => onDone(), 2000);
    } catch(e) { setError(e.message); }
    finally { setUploading(false); }
  };

  if (uploaded) return (
    <div className="rounded-2xl p-6 text-center" style={{background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.3)"}}>
      <div className="text-4xl mb-2">✅</div>
      <p className="text-green-300 font-semibold">Receipt uploaded!</p>
      <p className="text-gray-400 text-sm mt-1">We will verify and send your ticket within 2 hours.</p>
    </div>
  );

  return (
    <div className="rounded-2xl p-5" style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.1)"}}>
      <p className="text-white font-semibold text-sm mb-1">📤 Upload Payment Receipt</p>
      <p className="text-gray-400 text-xs mb-4">Order Ref: <span className="text-brand-300 font-mono">{orderRef}</span></p>
      <label className="block w-full cursor-pointer">
        <div className="rounded-xl p-6 text-center border-2 border-dashed transition-all"
          style={{borderColor: file ? "rgba(34,197,94,0.5)" : "rgba(255,255,255,0.1)"}}>
          {file ? (
            <div>
              <p className="text-green-400 font-medium text-sm">✅ {file.name}</p>
              <p className="text-gray-500 text-xs mt-1">{(file.size/1024).toFixed(0)} KB</p>
            </div>
          ) : (
            <div>
              <p className="text-3xl mb-2">📎</p>
              <p className="text-gray-300 text-sm">Click to select receipt</p>
              <p className="text-gray-500 text-xs mt-1">JPG, PNG or PDF • Max 5MB</p>
            </div>
          )}
        </div>
        <input type="file" accept="image/*,.pdf" className="hidden"
          onChange={e => setFile(e.target.files[0])} />
      </label>
      {error && <p className="text-red-400 text-xs mt-2">⚠️ {error}</p>}
      <button onClick={handleUpload} disabled={!file || uploading}
        className="w-full mt-4 py-3.5 rounded-2xl font-bold text-white transition-all disabled:opacity-50"
        style={{background:"linear-gradient(135deg,#166534,#16a34a)"}}>
        {uploading ? "Uploading..." : "Upload Receipt"}
      </button>
    </div>
  );
}

function BankReceiptUploadInline({ orderRef: existingRef, attendees, eventId, tierId, quantity, finalTotal, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!file) { setError("Please select your receipt first"); return; }
    setUploading(true); setError("");
    try {
      // Create order first if not exists
      let ref = existingRef;
      if (!ref) {
        const res = await fetch('/api/tickets/orders/bank-transfer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventId, tierId, quantity, attendees: attendees.map(a => ({name:a.name, whatsapp:a.whatsapp, email:a.email})) })
        });
        const d = await res.json();
        if (!d.success) throw new Error(d.error);
        ref = d.orderRef;
      }

      // Upload receipt
      const formData = new FormData();
      formData.append("receipt", file);
      const res2 = await fetch(`/api/tickets/orders/${ref}/upload-receipt`, {
        method: "POST", body: formData,
      });
      const d2 = await res2.json();
      if (!d2.success) throw new Error(d2.error);
      onDone();
    } catch(e) { setError(e.message); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <p className="text-white text-sm font-semibold mb-2">📤 Upload Payment Slip <span className="text-red-400">*</span></p>
      <label className="block w-full cursor-pointer mb-3">
        <div className="rounded-xl p-4 text-center border-2 border-dashed transition-all"
          style={{borderColor: file ? "rgba(34,197,94,0.6)" : "rgba(255,255,255,0.15)"}}>
          {file ? (
            <div>
              <p className="text-green-400 font-medium text-sm">✅ {file.name}</p>
              <p className="text-gray-500 text-xs mt-0.5">{(file.size/1024).toFixed(0)} KB — tap to change</p>
            </div>
          ) : (
            <div>
              <p className="text-2xl mb-1">📎</p>
              <p className="text-gray-300 text-sm">Tap to upload receipt</p>
              <p className="text-gray-500 text-xs mt-0.5">JPG, PNG or PDF · Max 5MB</p>
            </div>
          )}
        </div>
        <input type="file" accept="image/*,.pdf" className="hidden"
          onChange={e => setFile(e.target.files[0])} />
      </label>
      {error && <p className="text-red-400 text-xs mb-2">⚠️ {error}</p>}
      <button onClick={handleSubmit} disabled={uploading}
        className="w-full py-4 rounded-2xl font-bold text-white text-lg transition-all disabled:opacity-60"
        style={{background:"linear-gradient(135deg,#166534,#16a34a)", boxShadow:"0 8px 32px rgba(22,163,74,0.3)"}}>
        {uploading ? "Submitting..." : "Submit Bank Transfer →"}
      </button>
    </div>
  );
}
